/*
 * Copyright (c) 2016 Klaus Reimer
 * SPDX-License-Identifier: MIT
 */

/**
 * Reader for reading binary data in various formats and sizes.
 */
export class BinaryReader {
    /** Wrapped array to read from. */
    readonly #data: Uint8Array;

    /** The data size in bytes. */
    readonly #byteLength: number;

    /** The current byte index. */
    #byte: number;

    /** The current bit index. */
    #bit: number;

    /**
     * Creates a new binary reader for the given data.
     *
     * @param data   - The data array to read from.
     * @param offset - Optional start offset. Defaults to 0.
     * @param size   - Optional maximum number of bytes to read from data array. Defaults to size of data array minus the offset.
     */
    public constructor(data: Uint8Array, offset = 0, size = data.byteLength - offset) {
        this.#data = new Uint8Array(data.buffer, data.byteOffset + offset, size);
        this.#byteLength = size;
        this.#byte = 0;
        this.#bit = 0;
    }

    /**
     * Synchronizes the reader so it points to a full byte if it currently doesn't.
     *
     * @returns This reader for method chaining.
     */
    public sync(): this {
        if (this.#bit !== 0) {
            this.#byte++;
            this.#bit = 0;
        }
        return this;
    }

    /**
     * Seeks to the given index.
     *
     * @param byte - The byte index to set. Must not be outside of the data array range.
     * @param bit  - Optional bit index to set. Defaults to 0.
     * @returns This reader for method chaining.
     * @throws {@link !RangeError} if the index is out of bounds.
     */
    public seek(byte: number, bit = 0): this {
        // Normalize byte/bit offset
        byte += bit >> 3;
        bit &= 7;

        if (byte < 0 || byte > this.#byteLength) {
            throw new RangeError(`Invalid byte index: ${byte}`);
        } else if (byte === this.#byteLength && bit !== 0) {
            throw new RangeError(`Invalid byte index: ${byte} (+ ${bit} bit)`);
        }

        this.#byte = byte;
        this.#bit = bit;
        return this;
    }

    /**
     * Skips the specified number of bytes and bits.
     *
     * @param byte - The bytes to skip.
     * @param bit  - The bits to skip. Defaults to 0.
     * @returns This reader for method chaining.
     * @throws {@link !RangeError} if the index is out of bounds.
     */
    public skip(byte: number, bit = 0): this {
        return this.seek(this.#byte + byte, this.#bit + bit);
    }

    /**
     * Checks if there is enough data to read. If this method returns false then the next read operation with the same
     * length results in an error.
     *
     * @returns True if there is enough data to read, false if not.
     */
    public hasData(bytes = 1, bits = 0): boolean {
        return (this.#data.length - this.#byte << 3) - this.#bit >= bits + (bytes << 3);
    }

    /**
     * @returns The current byte index.
     */
    public getByteIndex(): number {
        return this.#byte;
    }

    /**
     * @returns The size of the data to read in bytes.
     */
    public getByteLength(): number {
        return this.#byteLength;
    }

    /**
     * @returns The current bit index.
     */
    public getBitIndex(): number {
        return this.#bit;
    }

    /**
     * Reads a single bit and returns it.
     *
     * @param reverse - Set to true to read bit in reverse order (Starting with the lowest bit instead of the highest bit). Defaults to false.
     * @returns The read bit.
     * @throws {@link !RangeError} if reached the end of the data.
     */
    public readBit(reverse = false): number {
        if (this.#byte >= this.#byteLength) {
            throw new RangeError("End of data");
        }
        const result = (reverse
            ? (this.#data[this.#byte] >> (this.#bit))
            : (this.#data[this.#byte] >> (7 - this.#bit))) & 1;
        this.#bit++;
        if (this.#bit > 7) {
            this.#bit = 0;
            this.#byte++;
        }
        return result;
    }

    /**
     * Reads the given number of bits and returns them.
     *
     * @param count   - The number of bits to read.
     * @param reverse - Set to true to read bits in reverse order (Starting with the lowest bit instead of the highest bit). Defaults to false.
     * @returns The read bits.
     * @throws {@link !RangeError} if reached the end of the data.
     */
    public readBits(count: number, reverse = false): number {
        let result = 0;
        for (let i = 0; i < count; i++) {
            const bit = this.readBit(reverse);
            result = reverse ? (result | (bit << i)) : ((result << 1) | bit);
        }
        return result;
    }

    /**
     * Reads an unsigned 8 bit value (byte) and returns it.
     *
     * @returns The read value.
     * @throws {@link !RangeError} if reached the end of the data.
     */
    public readUint8(): number {
        if (this.#bit !== 0) {
            // Slow lane: Need to construct the byte from two parts
            if (this.#byte + 1 >= this.#byteLength) {
                throw new RangeError("End of data");
            }
            return this.#data[this.#byte] << this.#bit & 0xff | this.#data[++this.#byte] >> 8 - this.#bit;
        } else {
            // Fast lane: Return byte directly
            if (this.#byte >= this.#byteLength) {
                throw new RangeError("End of data");
            }
            return this.#data[this.#byte++] & 0xff;
        }
    }

    /**
     * Reads a signed 8 bit value (byte) and returns it.
     *
     * @returns The read value.
     * @throws {@link !RangeError} if reached the end of the data.
     */
    public readInt8(): number {
        const unsigned = this.readUint8();
        return unsigned >= 0x80 ? unsigned - 0x100 : unsigned;
    }

    /**
     * Reads the specified number of unsigned 8 bit values (bytes) and returns them.
     *
     * @param count - The number of bytes to read.
     * @returns The read bytes.
     * @throws {@link !RangeError} if reached the end of the data.
     */
    public readUint8s(count: number): Uint8Array {
        let result: Uint8Array;
        if (this.#bit) {
            // Slow lane: Need to read the data byte by byte
            result = new Uint8Array(count);
            for (let i = 0; i < count; ++i) {
                result[i] = this.readUint8();
            }
        } else {
            // Fast lane: Read whole data block directly
            if (this.#byte + count > this.#byteLength) {
                throw new RangeError("End of data");
            }
            result = this.#data.slice(this.#byte, this.#byte += count);
        }
        return result;
    }

    /**
     * Reads an unsigned 16 bit value and returns it.
     *
     * @returns The read value.
     * @throws {@link !RangeError} if reached the end of the data.
     */
    public readUint16(): number {
        return this.readUint8() | this.readUint8() << 8;
    }

    /**
     * Reads the specified number of unsigned 16 bit values and returns them.
     *
     * @param count - he number of values to read.
     * @returns The read values.
     * @throws {@link !RangeError} if reached the end of the data.
     */
    public readUint16s(count: number): Uint16Array {
        let result: Uint16Array;
        if (this.#bit) {
            result = new Uint16Array(count);
            for (let i = 0; i < count; ++i) {
                result[i] = this.readUint16();
            }
        } else {
            result = new Uint16Array(this.#data.slice(this.#byte, this.#byte += count * 2).buffer);
        }
        return result;
    }

    /**
     * Reads an unsigned 32 bit value and returns it.
     *
     * @returns The read value.
     * @throws {@link !RangeError} if reached the end of the data.
     */
    public readUint32(): number {
        return this.readUint16() | this.readUint16() << 16;
    }

    /**
     * Reads an unsigned 24 bit value and returns it.
     *
     * @returns The read value.
     * @throws {@link !RangeError} if reached the end of the data.
     */
    public readUint24(): number {
        return this.readUint16() | this.readUint8() << 16;
    }

    /**
     * Reads a single character and returns it.
     *
     * @returns The read character.
     * @throws {@link !RangeError} if reached the end of the data.
     */
    public readChar(): string {
        return String.fromCharCode(this.readUint8());
    }

    /**
     * Reads a string with the given length and returns it.
     *
     * @param len - The length of the string to read.
     * @returns The read string.
     * @throws {@link !RangeError} if reached the end of the data.
     */
    public readString(len: number): string {
        let result = "";
        for (let i = 0; i < len; ++i) {
            result += this.readChar();
        }
        return result;
    }

    /**
     * Reads a null-terminated string and returns it.
     *
     * @param len - Optional string length. If not specified then bytes are read until a null-byte is found. If
     *              specified then reading stops at this length or (if null-byte was found before) the reader skips the
     *              remaining bytes so it is guaranteed that the reader read `len` bytes.
     * @returns The read string.
     */
    public readNullString(len?: number): string {
        let result = "";
        let read = 0;
        while (true) {
            const c = this.readUint8();
            read++;
            if (c === 0) {
                if (len != null) {
                    this.skip(len - read);
                }
                break;
            }
            result += String.fromCharCode(c);
            if (len != null && read >= len) {
                break;
            }
        }
        return result;
    }

    /**
     * Reads the specified number of null-terminated strings and returns them.
     *
     * @param num - The number of null-terminated strings to read.
     * @returns The read strings.
     */
    public readNullStrings(num: number): string[] {
        const result: string[] = [];
        for (let i = 0; i < num; ++i) {
            result.push(this.readNullString());
        }
        return result;
    }
}
